import React, { useState, useMemo, useEffect } from 'react';
import { BarChart2, Users, Loader, ChevronDown, Search, UtensilsCrossed, Clock } from 'lucide-react';
import { getReporte } from '../../api/cafeteria';

const OPTION_COLORS = [
  { bg: 'bg-blue-50',   text: 'text-blue-700',   bar: 'bg-blue-500',   badge: 'bg-blue-100 text-blue-700'   },
  { bg: 'bg-orange-50', text: 'text-orange-700',  bar: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700' },
  { bg: 'bg-violet-50', text: 'text-violet-700',  bar: 'bg-violet-500', badge: 'bg-violet-100 text-violet-700' },
  { bg: 'bg-teal-50',   text: 'text-teal-700',    bar: 'bg-teal-500',   badge: 'bg-teal-100 text-teal-700'   },
];

function fechaLabel(fechaRaw) {
  const str = (fechaRaw || '').split('T')[0];
  return new Date(str + 'T00:00:00').toLocaleDateString('es-DO', { weekday: 'long', day: 'numeric', month: 'long' });
}

function menuSummary(op) {
  return [op.principal, op.proteina, op.acompanamiento1, op.acompanamiento2, op.ensalada]
    .filter(Boolean).join(' · ') || op.nombre || '—';
}

function initials(nombre) {
  return (nombre || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

export default function ReporteCafeteria({ menus }) {
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [reporte,        setReporte]        = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [filtroEmpleado, setFiltroEmpleado] = useState('');

  useEffect(() => {
    if (!menus || menus.length === 0) return;
    const today = new Date().toLocaleDateString('sv-SE');
    const todayMenu = menus.find(m => (m.fecha || '').split('T')[0] === today);
    if (todayMenu) cargar(todayMenu.id);
  }, [menus]); // eslint-disable-line

  const cargar = async (id) => {
    if (!id) return;
    setSelectedMenuId(id);
    setLoading(true);
    setError(null);
    setReporte(null);
    setFiltroEmpleado('');
    try {
      const data = await getReporte(id);
      setReporte(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar reporte');
    } finally {
      setLoading(false);
    }
  };

  const cerrados = useMemo(() =>
    menus
      .filter(m => m.estado === 'closed' || m.estado === 'published')
      .sort((a, b) => ((b.fecha || '').split('T')[0]).localeCompare((a.fecha || '').split('T')[0])),
    [menus]
  );

  const selecciones = useMemo(() => {
    const rows = [];
    for (const op of (reporte?.opciones || []))
      for (const emp of (op.empleados || []))
        rows.push({ emp, op });
    const filtradas = filtroEmpleado.trim()
      ? rows.filter(r => (r.emp.nombre || '').toLowerCase().includes(filtroEmpleado.toLowerCase()))
      : rows;
    return filtradas.sort((a, b) => (a.emp.nombre || '').localeCompare(b.emp.nombre || ''));
  }, [reporte, filtroEmpleado]);

  return (
    <div className="space-y-3">

      {/* ── Toolbar ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <select
            value={selectedMenuId}
            onChange={e => cargar(e.target.value)}
            className="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none text-gray-700"
          >
            <option value="">Selecciona un menú...</option>
            {cerrados.map(m => (
              <option key={m.id} value={m.id}>
                {fechaLabel(m.fecha)}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2.5 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {loading && <Loader size={14} className="animate-spin text-blue-500 shrink-0" />}
        {error && <p className="text-xs text-red-500">{error}</p>}

        {cerrados.length === 0 && (
          <p className="text-xs text-gray-400">No hay menús disponibles</p>
        )}
      </div>

      {/* ── KPI strip ─────────────────────────────────────────────── */}
      {reporte && (() => {
        const fechaStr = (reporte.menu.fecha || '').split('T')[0];
        const fechaDisplay = new Date(fechaStr + 'T00:00:00').toLocaleDateString('es-DO', {
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
        });
        return (
          <div className="flex items-center gap-4 px-4 py-2.5 bg-white border border-gray-100 rounded-lg text-sm flex-wrap">
            <span className="font-semibold text-gray-800 capitalize">{fechaDisplay}</span>
            <span className="text-gray-200">|</span>
            <span className="flex items-center gap-1.5 text-gray-500">
              <Users size={13} className="text-blue-400" />
              <span className="font-semibold text-gray-800">{reporte.total_selecciones}</span> selecciones
            </span>
            <span className="text-gray-200">|</span>
            <span className="flex items-center gap-1.5 text-gray-500">
              <UtensilsCrossed size={13} className="text-orange-400" />
              <span className="font-semibold text-gray-800">{reporte.opciones?.length || 0}</span> opciones
            </span>
            <span className="text-gray-200">|</span>
            <span className="flex items-center gap-1.5 text-gray-500">
              <Clock size={13} className="text-gray-400" />
              Límite {reporte.menu.hora_limite?.slice(0, 5)}
            </span>
            <span className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full ${
              reporte.menu.estado === 'published'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-500'
            }`}>
              {reporte.menu.estado === 'published' ? 'Abierto' : 'Cerrado'}
            </span>
          </div>
        );
      })()}

      {/* ── Content ───────────────────────────────────────────────── */}
      {reporte && (
        <div>
          <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex-1">Empleados</span>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={filtroEmpleado}
                  onChange={e => setFiltroEmpleado(e.target.value)}
                  placeholder="Buscar..."
                  className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 w-40"
                />
              </div>
              {filtroEmpleado && (
                <span className="text-xs text-gray-400">{selecciones.length} resultado{selecciones.length !== 1 ? 's' : ''}</span>
              )}
            </div>

            {/* List */}
            {selecciones.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">
                  {filtroEmpleado ? `Sin resultados para "${filtroEmpleado}"` : 'Sin selecciones registradas'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {selecciones.map(({ emp, op }) => {
                  const colorIdx = (reporte.opciones || []).findIndex(o => o.opcion_id === op.opcion_id);
                  const color = OPTION_COLORS[colorIdx % OPTION_COLORS.length];
                  const items = [
                    op.principal       && { label: 'Principal',   value: op.principal       },
                    op.proteina        && { label: 'Proteína',     value: op.proteina        },
                    op.acompanamiento1 && { label: 'Acomp.',       value: op.acompanamiento1 },
                    op.acompanamiento2 && { label: 'Acomp. 2',     value: op.acompanamiento2 },
                    op.ensalada        && { label: 'Ensalada',      value: op.ensalada        },
                    op.bebida          && { label: 'Bebida',        value: op.bebida          },
                    op.postre          && { label: 'Postre',        value: op.postre          },
                  ].filter(Boolean);
                  return (
                    <div key={`${emp.employee_id}-${op.opcion_id}`}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/60 transition-colors">
                      {/* Avatar */}
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0 mt-0.5">
                        {initials(emp.nombre)}
                      </div>
                      {/* Name + detail */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{emp.nombre || '—'}</p>
                        {items.length > 0 ? (
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                            {items.map(({ label, value }) => (
                              <span key={label} className="text-xs text-gray-500">
                                <span className="text-gray-400">{label}:</span> {value}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 mt-0.5">{op.nombre || '—'}</p>
                        )}
                      </div>
                      {/* Option badge */}
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${color.badge}`}>
                        Op. {op.numero_opcion}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer count */}
            {selecciones.length > 0 && (
              <div className="px-4 py-2 border-t border-gray-50 text-xs text-gray-400">
                {selecciones.length} empleado{selecciones.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Empty state ───────────────────────────────────────────── */}
      {!reporte && !loading && cerrados.length === 0 && (
        <div className="bg-white border border-gray-100 rounded-lg py-16 text-center">
          <BarChart2 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">No hay menús para reportar</p>
          <p className="text-xs text-gray-400 mt-1">Los reportes aparecen cuando hay menús publicados o cerrados</p>
        </div>
      )}

      {!reporte && !loading && cerrados.length > 0 && !selectedMenuId && (
        <div className="bg-white border border-gray-100 rounded-lg py-12 text-center">
          <BarChart2 className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Selecciona un menú para ver el reporte</p>
        </div>
      )}
    </div>
  );
}
